import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Image as ImageIcon, BookOpen, Music, Feather, Layers,
  Sparkles, Loader2, Download, RefreshCw, Check, X,
  Key, Info, AlertCircle, Calculator, Wand2, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { id: 'book_cover', name: 'Book Cover', icon: BookOpen, blurb: 'KDP-ready, print quality' },
  { id: 'album_cover', name: 'Album Cover', icon: Music, blurb: '3000×3000 square' },
  { id: 'poem_art', name: 'Poem Art', icon: Feather, blurb: 'Inspired by your verse' },
  { id: 'lyric_art', name: 'Lyric Art', icon: Music, blurb: 'Visual for a song' },
  { id: 'custom', name: 'Custom', icon: Layers, blurb: 'Your own dimensions' },
];

const STYLES = [
  'painterly', 'photorealistic', 'cinematic', 'watercolor', 'oil painting',
  'minimalist', 'vintage', 'noir', 'dreamlike', 'abstract', 'surreal',
  'art deco', 'gothic', 'pastoral', 'dark fantasy', 'editorial illustration'
];

const MOODS = [
  'evocative', 'haunting', 'hopeful', 'tender', 'fierce', 'melancholic',
  'mystical', 'romantic', 'tense', 'serene', 'raw', 'triumphant'
];

const ImageCreatorSection = ({ manuscript, title, writingType }) => {
  const [mode, setMode] = useState('book_cover');
  const [apiKey, setApiKey] = useState('');
  const [userIdea, setUserIdea] = useState('');
  const [style, setStyle] = useState('painterly');
  const [mood, setMood] = useState('evocative');
  const [quality, setQuality] = useState('high');

  // Book cover specifics
  const [presets, setPresets] = useState({ trim_sizes: [], paper_types: [], formats: [] });
  const [formatType, setFormatType] = useState('paperback');
  const [trimSize, setTrimSize] = useState('6x9');
  const [pageCount, setPageCount] = useState(280);
  const [paperType, setPaperType] = useState('white');
  const [fullWrap, setFullWrap] = useState(false);
  const [kdpResult, setKdpResult] = useState(null);

  // Custom dimensions
  const [customW, setCustomW] = useState(2400);
  const [customH, setCustomH] = useState(3000);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]); // [{id, base64, dimensions, prompt, mode}]
  const [currentIdx, setCurrentIdx] = useState(-1);

  useEffect(() => {
    loadPresets();
  }, []);

  useEffect(() => {
    if (mode === 'book_cover') {
      calculateKdp();
    } else {
      setKdpResult(null);
    }
    // eslint-disable-next-line
  }, [mode, formatType, trimSize, pageCount, paperType, fullWrap]);

  const loadPresets = async () => {
    try {
      const r = await axios.get(`${API}/image/book-presets`);
      setPresets(r.data);
    } catch (e) {
      console.error('preset load error', e);
    }
  };

  const getTrim = () => presets.trim_sizes.find(t => t.id === trimSize) || { w: 6, h: 9 };

  const calculateKdp = async () => {
    if (!presets.trim_sizes.length) return;
    const trim = getTrim();
    try {
      const r = await axios.post(`${API}/image/kdp-calculate`, {
        format_type: formatType,
        trim_w: trim.w,
        trim_h: trim.h,
        page_count: pageCount,
        paper_type: paperType,
        full_wrap: fullWrap,
      });
      setKdpResult(r.data);
    } catch (e) {
      console.error('KDP calc error', e);
    }
  };

  const generate = async () => {
    setError(null);

    if (!apiKey.trim()) {
      setError('Please paste your OpenAI API key. It is used only for this generation, never stored.');
      return;
    }

    setIsGenerating(true);

    try {
      const trim = getTrim();
      const payload = {
        api_key: apiKey,
        mode,
        user_idea: userIdea,
        title: title || '',
        style,
        mood,
        quality,
        manuscript_excerpt: manuscript ? manuscript.slice(0, 1500) : '',
        writing_type: writingType || '',
        format_type: formatType,
        trim_w: trim.w,
        trim_h: trim.h,
        page_count: pageCount,
        paper_type: paperType,
        full_wrap: fullWrap,
      };

      if (mode === 'custom' || mode === 'poem_art' || mode === 'lyric_art') {
        payload.target_width = customW;
        payload.target_height = customH;
      }

      const r = await axios.post(`${API}/image/generate`, payload);

      const item = {
        id: Date.now(),
        base64: r.data.image_base64,
        dimensions: r.data.dimensions,
        prompt: r.data.prompt_used,
        mode: r.data.mode,
        kdp: r.data.kdp,
      };

      setHistory(prev => [item, ...prev]);
      setCurrentIdx(0);
    } catch (e) {
      console.error('generation error', e);
      setError(e.response?.data?.detail || 'Generation failed. Check your API key and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const current = currentIdx >= 0 ? history[currentIdx] : null;

  const downloadCurrent = () => {
    if (!current) return;
    const a = document.createElement('a');
    a.href = `data:image/png;base64,${current.base64}`;
    const filenameBase = (title || mode).replace(/\s+/g, '_');
    a.download = `${filenameBase}_${current.dimensions.width}x${current.dimensions.height}.png`;
    a.click();
  };

  const denyCurrent = () => {
    // Remove current from history
    if (currentIdx < 0) return;
    const newHistory = history.filter((_, i) => i !== currentIdx);
    setHistory(newHistory);
    setCurrentIdx(newHistory.length > 0 ? 0 : -1);
  };

  return (
    <div className="image-creator-section" data-testid="image-creator-section">
      {/* Mode tabs */}
      <div className="image-mode-tabs" data-testid="image-mode-tabs">
        {MODES.map(m => (
          <button
            key={m.id}
            className={`image-mode-tab ${mode === m.id ? 'active' : ''}`}
            onClick={() => setMode(m.id)}
            data-testid={`image-mode-${m.id}`}
          >
            <m.icon size={20} />
            <span className="mode-name">{m.name}</span>
            <span className="mode-blurb">{m.blurb}</span>
          </button>
        ))}
      </div>

      {/* API key (BYOK) */}
      <div className="image-byok-card">
        <div className="byok-header">
          <Key size={18} />
          <span>Your OpenAI API Key (BYOK)</span>
        </div>
        <p className="byok-note">
          Image generation uses your own OpenAI key — keeps it private, predictable, and lets us run this app at no cost to you. Use the same key from the Audiobook step if you've set one up.
        </p>
        <input
          type="password"
          className="api-key-input"
          placeholder="sk-..."
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          data-testid="image-api-key-input"
        />
      </div>

      {/* Book cover form */}
      {mode === 'book_cover' && (
        <div className="book-cover-form">
          <h4><Calculator size={16} /> KDP-Ready Math (we handle this for you)</h4>

          <div className="form-row">
            <div className="form-field">
              <label>Format</label>
              <select value={formatType} onChange={e => setFormatType(e.target.value)} data-testid="format-select">
                {presets.formats.map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Trim Size</label>
              <select value={trimSize} onChange={e => setTrimSize(e.target.value)} data-testid="trim-select">
                {presets.trim_sizes.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formatType !== 'ebook' && (
            <div className="form-row">
              <div className="form-field">
                <label>Page Count</label>
                <input
                  type="number"
                  value={pageCount}
                  onChange={e => setPageCount(parseInt(e.target.value || '0'))}
                  min={24}
                  data-testid="page-count-input"
                />
              </div>
              <div className="form-field">
                <label>Paper Type</label>
                <select value={paperType} onChange={e => setPaperType(e.target.value)}>
                  {presets.paper_types.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field checkbox-field">
                <label>
                  <input
                    type="checkbox"
                    checked={fullWrap}
                    onChange={e => setFullWrap(e.target.checked)}
                  />
                  <span>Full wrap (front + spine + back)</span>
                </label>
              </div>
            </div>
          )}

          {kdpResult && (
            <div className="kdp-output">
              <div className="kdp-stat">
                <span className="kdp-label">Print-ready pixels</span>
                <span className="kdp-value">
                  {kdpResult.pixel_dimensions.width.toLocaleString()} × {kdpResult.pixel_dimensions.height.toLocaleString()}
                </span>
              </div>
              {kdpResult.spine_inches !== undefined && kdpResult.spine_inches > 0 && (
                <div className="kdp-stat">
                  <span className="kdp-label">Spine width</span>
                  <span className="kdp-value">
                    {kdpResult.spine_inches.toFixed(3)}″
                    {kdpResult.spine_mm ? ` (${kdpResult.spine_mm} mm)` : ''}
                  </span>
                </div>
              )}
              <div className="kdp-stat">
                <span className="kdp-label">DPI</span>
                <span className="kdp-value">{kdpResult.dpi}</span>
              </div>
              <div className="kdp-stat">
                <span className="kdp-label">Mode</span>
                <span className="kdp-value">{kdpResult.mode.replace('_', ' ')}</span>
              </div>
              {kdpResult.notes && kdpResult.notes.map((n, i) => (
                <div key={i} className="kdp-note"><Info size={13} /> {n}</div>
              ))}
              {kdpResult.warnings && kdpResult.warnings.map((w, i) => (
                <div key={i} className="kdp-warning"><AlertCircle size={13} /> {w}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom / poem / lyric dimension picker */}
      {(mode === 'custom' || mode === 'poem_art' || mode === 'lyric_art') && (
        <div className="custom-dim-form">
          <div className="form-row">
            <div className="form-field">
              <label>Width (px)</label>
              <input
                type="number"
                value={customW}
                onChange={e => setCustomW(parseInt(e.target.value || '0'))}
                min={512}
                max={6000}
                data-testid="custom-width"
              />
            </div>
            <div className="form-field">
              <label>Height (px)</label>
              <input
                type="number"
                value={customH}
                onChange={e => setCustomH(parseInt(e.target.value || '0'))}
                min={512}
                max={6000}
                data-testid="custom-height"
              />
            </div>
            <div className="form-field aspect-presets">
              <label>Quick aspects</label>
              <div className="aspect-buttons">
                <button onClick={() => { setCustomW(2400); setCustomH(3000); }}>4:5</button>
                <button onClick={() => { setCustomW(3000); setCustomH(3000); }}>1:1</button>
                <button onClick={() => { setCustomW(3000); setCustomH(2000); }}>3:2</button>
                <button onClick={() => { setCustomW(1080); setCustomH(1920); }}>9:16</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Concept + style + mood */}
      <div className="image-prompt-form">
        <div className="form-field">
          <label>Concept / Vision (optional, but recommended)</label>
          <textarea
            value={userIdea}
            onChange={e => setUserIdea(e.target.value)}
            placeholder="Describe what you see. e.g., 'A weathered hand reaching toward dawn through prison bars, hope blooming in cracked stone'..."
            className="concept-textarea"
            data-testid="image-concept-input"
          />
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Style</label>
            <select value={style} onChange={e => setStyle(e.target.value)} data-testid="image-style-select">
              {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Mood</label>
            <select value={mood} onChange={e => setMood(e.target.value)} data-testid="image-mood-select">
              {MOODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>Quality</label>
            <select value={quality} onChange={e => setQuality(e.target.value)}>
              <option value="low">Low (cheap, ~$0.01)</option>
              <option value="medium">Medium (~$0.04)</option>
              <option value="high">High (~$0.17)</option>
            </select>
          </div>
        </div>

        <div className="manuscript-aware-note">
          <Sparkles size={14} />
          <span>
            {manuscript && manuscript.length > 100
              ? "We'll weave the soul of your manuscript into the image."
              : "Add writing in Step 1 to make the image draw from your work."}
          </span>
        </div>
      </div>

      {error && (
        <div className="image-error" data-testid="image-error">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="image-actions">
        <button
          className="primary-btn"
          onClick={generate}
          disabled={isGenerating}
          data-testid="generate-image-btn"
        >
          {isGenerating ? (
            <><Loader2 className="spinning" size={18} /><span>Generating... (30–60s)</span></>
          ) : (
            <><Wand2 size={18} /><span>Generate Image</span></>
          )}
        </button>
      </div>

      {/* Preview + approve/regenerate/deny */}
      {current && (
        <div className="image-preview-card" data-testid="image-preview-card">
          <div className="preview-header">
            <h4><Eye size={16} /> Preview</h4>
            <span className="preview-dims">
              {current.dimensions.width.toLocaleString()} × {current.dimensions.height.toLocaleString()} px @ {current.dimensions.dpi} DPI
            </span>
          </div>
          <div className="preview-image-wrap">
            <img
              src={`data:image/png;base64,${current.base64}`}
              alt="Generated artwork"
              className="preview-image"
              data-testid="preview-image"
            />
          </div>
          <div className="preview-actions">
            <button className="approve-btn" onClick={downloadCurrent} data-testid="approve-btn">
              <Check size={16} /> Approve & Download
            </button>
            <button className="regenerate-btn" onClick={generate} disabled={isGenerating} data-testid="regenerate-btn">
              <RefreshCw size={16} /> Regenerate
            </button>
            <button className="deny-btn" onClick={denyCurrent} data-testid="deny-btn">
              <X size={16} /> Discard
            </button>
          </div>
          <details className="prompt-details">
            <summary>View prompt sent to OpenAI</summary>
            <p className="prompt-text">{current.prompt}</p>
          </details>
        </div>
      )}

      {/* History thumbnails */}
      {history.length > 1 && (
        <div className="image-history">
          <h4>This session ({history.length})</h4>
          <div className="history-thumbs">
            {history.map((h, i) => (
              <button
                key={h.id}
                className={`thumb ${i === currentIdx ? 'active' : ''}`}
                onClick={() => setCurrentIdx(i)}
                title={`${h.dimensions.width}×${h.dimensions.height}`}
              >
                <img src={`data:image/png;base64,${h.base64}`} alt="" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCreatorSection;
